import { shallowRef, computed } from "vue"
import { defineStore } from "pinia"

import { logError } from "@Helpers/logger.js"
import ShoppingCartModel from "../Data/Models/ShoppingCartModel"
import type { AddDomainParams, CartApi } from "../Api/CartApi"
import SingleUserCartApi from "../Api/SingleUserCartApi"
import TeamCartApi from "../Api/TeamCartApi"
import type { Organization } from "@Modules/teams/Data/Models/OrganizationModel"
import type { Team } from "@Modules/teams/Data/Models/TeamModel"
import { emptyTypedShoppingCart } from "../TestHelpers/FakeShoppingCart"

const emptyCart = ShoppingCartModel(emptyTypedShoppingCart())

export const XShoppingCartStore = defineStore("x-shopping-cart", () => {

  //
  // Data
  const activeCartApi = shallowRef<CartApi | null>(null)

  //
  // Computed
  const cart = computed(() => activeCartApi.value?.cart.value ?? emptyCart)
  const items = computed(() => cart.value.items)
  const quantity = computed(() => cart.value.quantity)
  const total = computed(() => cart.value.total)
  const price = computed(() => cart.value.price)
  const discount = computed(() => cart.value.discount)
  const isEmpty = computed(() => cart.value.isEmpty())
  const loading = computed(() => activeCartApi.value?.loading.value ?? false)
  const initialized = computed(() => activeCartApi.value?.initialized.value ?? false)

  //
  // Methods
  async function initializeCart(): Promise<void> {
    console.log("[Cart] Initializing user cart")
    activeCartApi.value = SingleUserCartApi()
    await activeCartApi.value.initialize()
  }

  async function initializeTeamCart(organization: Organization, team?: Team): Promise<void> {
    if (!organization.can("manage_org_domains")) {
      console.log("[Cart] Not loading cart - user lacks manage_org_domains permission for org:", organization.slug)
      return
    }

    console.log("[Cart] Initializing team cart for org:", organization.slug, team ? `team: ${team.slug}` : "")
    activeCartApi.value = TeamCartApi(organization.slug, team?.slug)
    await activeCartApi.value.initialize()
  }

  async function addDomain(params: AddDomainParams): Promise<boolean> {
    if (!activeCartApi.value) {
      logError("XShoppingCartStore: addDomain called but no cart is initialized")
      return false
    }
    return await activeCartApi.value.addDomain(params)
  }

  async function removeItem(rowId: string): Promise<boolean> {
    if (!activeCartApi.value) {
      logError("XShoppingCartStore: removeItem called but no cart is initialized")
      return false
    }
    return await activeCartApi.value.removeItem(rowId)
  }

  function clearCart(): void {
    if (activeCartApi.value) {
      activeCartApi.value.clearCart()
    }
  }

  async function refreshCart(): Promise<void> {
    if (!activeCartApi.value) {
      logError("XShoppingCartStore: refreshCart called but no cart is initialized")
      return
    }
    await activeCartApi.value.refreshCart()
  }

  return {
    cart,
    items,
    quantity,
    total,
    price,
    discount,
    isEmpty,
    loading,
    initialized,
    initializeCart,
    initializeTeamCart,
    addDomain,
    removeItem,
    clearCart,
    refreshCart,
  }
})
